import axios from "axios";
import { invalidateCache } from "./persistCache";

/**
 * Runtime API base resolution.
 *
 * Why we don't trust `process.env.REACT_APP_BACKEND_URL` blindly:
 *  Emergent's deployment pipeline auto-overwrites that build-time variable
 *  to point at the .emergent.host hostname even when the app is served
 *  from a custom domain (e.g. builddirectafrica.com). That breaks CORS
 *  and authentication for visitors on the custom domain.
 *
 * Fix: in the browser, use `window.location.origin` so API calls always
 *      hit the same hostname the user is currently on. The backend lives
 *      at /api/* on every Emergent deployment, so this is always correct.
 *      Falls back to REACT_APP_BACKEND_URL during SSR / build / Node tests.
 */
const RAW_BACKEND_URL = (typeof window !== "undefined" && window.location?.origin)
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || "");

const BACKEND_URL = RAW_BACKEND_URL.replace(/\/+$/, "");
export const API = `${BACKEND_URL}/api`;

// Persistent device ID for anonymous engagement
function getDeviceId() {
  let id = localStorage.getItem("bda_device");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("bda_device", id);
  }
  return id;
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    "X-Device-Id": getDeviceId(),
  },
});

// Attach token from localStorage as fallback (cookie also works)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("bda_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Auto-invalidate the persisted projects cache whenever a write hits the
 * projects API. This guarantees the admin grid and the public landing page
 * always converge to the same fresh data on the next render — eliminates
 * the "admin grid doesn't match frontend" divergence caused by stale
 * localStorage snapshots after slot uploads / edits / deletes.
 */
api.interceptors.response.use((response) => {
  try {
    const method = (response.config?.method || "").toLowerCase();
    const url = response.config?.url || "";
    const isWrite = ["post", "patch", "put", "delete"].includes(method);
    const touchesProjects = /\/projects(\/|$)/.test(url) || /\/admin\/projects/.test(url) || url.endsWith("/upload");
    const touchesCategories = /\/categories(\/|$)/.test(url);
    const touchesSettings = /\/settings(\/|$)/.test(url);
    if (isWrite && touchesProjects) invalidateCache("projects:");
    if (isWrite && touchesCategories) invalidateCache("categories");
    if (isWrite && touchesSettings) invalidateCache("settings");
  } catch {
    // never let cache-busting break the response
  }
  return response;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function fileUrl(urlOrPath) {
  if (!urlOrPath) return "";
  if (urlOrPath.startsWith("http")) return urlOrPath;
  if (urlOrPath.startsWith("/api/")) return `${BACKEND_URL}${urlOrPath}`;
  return `${BACKEND_URL}/api/files/${urlOrPath}`;
}
